// Portfolio Administration v1.0 PR 5 — cross-portfolio Local Enforcer
// registry detail page at /portfolio-admin/local-enforcers/:enforcerId.
// Renders the 13-tab Entity Detail Page Layout v1.1 §3
// (Overview / Qualifications / Engagements / Agreements / Cost Ledger /
// Performance + 6 cross-cutting). All entity-specific tabs ship with
// real content using the rich LocalEnforcerDetailDto + the existing
// PR 2b/PR 4b services.

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BadgeCheck,
  Briefcase,
  DollarSign,
  FileText as FileTextIcon,
  Star,
} from "lucide-react";
import { Card } from "@/design-system/components/ui";
import {
  CROSS_CUTTING_TAB_ICONS,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useLocalEnforcerDetail } from "@/hooks/portfolioAdmin/useRegistries";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import {
  RegistryDetailLayout,
  useRegistryTabState,
} from "@/components/portfolioAdmin/registry/RegistryDetailLayout";
import { LocalEnforcerDetailHeader } from "@/components/portfolioAdmin/localEnforcers/LocalEnforcerDetailHeader";
// Attach-record expansion (WS6) — replace the EmptyTabCard stubs with
// the shared Messages + Tasks panels now that LocalEnforcer is
// registered on both surfaces.
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { EnforcerOverviewTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerOverviewTab";
import { EnforcerQualificationsTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerQualificationsTab";
import { EnforcerEngagementsTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerEngagementsTab";
import { EnforcerAgreementsTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerAgreementsTab";
import { EnforcerCostLedgerTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerCostLedgerTab";
import { EnforcerPerformanceTab } from "@/components/portfolioAdmin/localEnforcers/tabs/EnforcerPerformanceTab";

export type EnforcerTabId =
  | "overview"
  | "qualifications"
  | "engagements"
  | "agreements"
  | "cost-ledger"
  | "performance"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly EnforcerTabId[] = [
  "overview",
  "qualifications",
  "engagements",
  "agreements",
  "cost-ledger",
  "performance",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<EnforcerTabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "qualifications", label: "Qualifications", icon: BadgeCheck },
  { key: "engagements", label: "Engagements", icon: Briefcase },
  { key: "agreements", label: "Agreements", icon: FileTextIcon },
  { key: "cost-ledger", label: "Cost Ledger", icon: DollarSign },
  { key: "performance", label: "Performance", icon: Star },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];

export default function LocalEnforcerDetailPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Local Enforcers"
        description="Workout counterparty registry."
      />
    );
  }
  return <LocalEnforcerDetailPageInner />;
}

function LocalEnforcerDetailPageInner() {
  const { enforcerId } = useParams<{ enforcerId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useRegistryTabState<EnforcerTabId>(
    TABS,
    params,
    setParams,
  );

  const detailQuery = useLocalEnforcerDetail(enforcerId);
  const enforcer = detailQuery.data;

  if (detailQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading local enforcer…
        </p>
      </Card>
    );
  }

  if (!enforcer) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">
          Local enforcer not found
        </h2>
        <p className="text-small text-gray-mid">
          The local enforcer you requested was not found or you don&apos;t
          have permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/portfolio-admin/local-enforcers")}
        >
          Back to Local Enforcers
        </button>
      </Card>
    );
  }

  return (
    <RegistryDetailLayout
      testIdPrefix="enforcer"
      tabsAriaLabel="Local enforcer sections"
      header={<LocalEnforcerDetailHeader enforcer={enforcer} />}
      tabItems={TAB_ITEMS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "overview" && <EnforcerOverviewTab enforcer={enforcer} />}
      {tab === "qualifications" && (
        <EnforcerQualificationsTab enforcer={enforcer} />
      )}
      {tab === "engagements" && (
        <EnforcerEngagementsTab enforcerId={enforcer.id} />
      )}
      {tab === "agreements" && <EnforcerAgreementsTab />}
      {tab === "cost-ledger" && <EnforcerCostLedgerTab />}
      {tab === "performance" && (
        <EnforcerPerformanceTab enforcer={enforcer} />
      )}
      {tab === "documents" && (
        <DocumentPanel ownerType="LocalEnforcer" ownerId={enforcer.id} />
      )}
      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="LocalEnforcer"
          relatedEntityId={enforcer.id}
          addButtonLabel="+ Add task"
        />
      )}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="LocalEnforcer"
          relatedEntityId={enforcer.id}
          entityLabel={enforcer.dbaName || enforcer.legalEntityName}
        />
      )}
      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="local_enforcer"
          resourceId={enforcer.id}
          entityLabel={enforcer.dbaName || enforcer.legalEntityName}
        />
      )}
      {tab === "communications" && (
        <CommunicationsTabPanel
          ownerType="local_enforcer"
          ownerId={enforcer.id}
        />
      )}
      {tab === "notes" && (
        <NotesTabPanel ownerType="local_enforcer" ownerId={enforcer.id} />
      )}
      {tab === "activity" && (
        <ActivityTabPanel ownerType="local_enforcer" ownerId={enforcer.id} />
      )}
    </RegistryDetailLayout>
  );
}
